import * as THREE from "three";
import { MeshBVH } from "three-mesh-bvh";

/**
 * Removes triangles intersecting a world-space sphere from multi-mesh models.
 * Works safely with indexed & non-indexed BufferGeometries.
 */
export function removeTrianglesInSphere(obj, worldSphere) {
  const meshes = [];
  obj.traverse((child) => {
    if (child.isMesh && child.geometry?.attributes?.position) meshes.push(child);
  });

  if (meshes.length === 0) {
    console.warn("❌ No meshes found in", obj);
    return null;
  }

  console.log(`🧩 Found ${meshes.length} mesh(es) in "${obj.name}"`);
  const removedChunks = [];

  for (const mesh of meshes) {
    mesh.updateMatrixWorld(true);
    const geom = mesh.geometry;
    const pos = geom.attributes.position;
    const index = geom.index;
    const uvAttr = geom.attributes.uv;
    const groups = geom.groups || [];

    // Build BVH if missing
    if (!geom.boundsTree) {
      console.log(`Building BVH for "${mesh.name}"...`);
      geom.boundsTree = new MeshBVH(geom);
    }

    // Convert world sphere → local
    const localSphere = worldSphere.clone();
    mesh.worldToLocal(localSphere.center);
    const scale = new THREE.Vector3();
    mesh.getWorldScale(scale);
    const maxAxis = Math.max(scale.x, scale.y, scale.z) || 1;
    localSphere.radius = worldSphere.radius / maxAxis;

    // Total triangle count
    const triCount = index ? index.count / 3 : pos.count / 3;
    const triMask = new Uint8Array(triCount); // 1 = remove

    let removedTris = 0;
    geom.boundsTree.shapecast({
      intersectsBounds: (box) => localSphere.intersectsBox(box),
      intersectsTriangle: (tri, triIndex) => {
        const hit =
          localSphere.containsPoint(tri.a) ||
          localSphere.containsPoint(tri.b) ||
          localSphere.containsPoint(tri.c);
        if (hit) {
          triMask[triIndex] = 1;
          removedTris++;
        }
        return false;
      },
    });

    console.log(`✅ ${removedTris} / ${triCount} triangles removed from "${mesh.name}"`);

    if (removedTris === 0) continue;

    // Build new geometry: keep unmasked triangles only
    const keepPositions = [];
    const keepUVs = [];
    const removedPositions = [];
    const removedUVs = [];

    const getMatIndex = (first) => {
      if (!groups.length) return 0;
      for (const g of groups) {
        const start = g.start;
        const end = start + g.count;
        if (first >= start && first < end) return g.materialIndex ?? 0;
      }
      return 0;
    };

    const removedByMat = new Map();
    const bucket = (mIdx) => {
      if (!removedByMat.has(mIdx)) removedByMat.set(mIdx, { positions: [], uvs: [] });
      return removedByMat.get(mIdx);
    };

    for (let i = 0; i < triCount; i++) {
      const a = index ? index.getX(i * 3) : i * 3;
      const b = index ? index.getX(i * 3 + 1) : i * 3 + 1;
      const c = index ? index.getX(i * 3 + 2) : i * 3 + 2;
      const matIndex = getMatIndex(i * 3);

      const vA = new THREE.Vector3().fromBufferAttribute(pos, a);
      const vB = new THREE.Vector3().fromBufferAttribute(pos, b);
      const vC = new THREE.Vector3().fromBufferAttribute(pos, c);

      const tri = new THREE.Triangle(vA, vB, vC);
      const hit =
        localSphere.containsPoint(vA) ||
        localSphere.containsPoint(vB) ||
        localSphere.containsPoint(vC);

      const pushTri = (dst, uvDst) => {
        dst.push(vA.x, vA.y, vA.z, vB.x, vB.y, vB.z, vC.x, vC.y, vC.z);
        if (uvAttr) {
          const uvA = new THREE.Vector2().fromBufferAttribute(uvAttr, a);
          const uvB = new THREE.Vector2().fromBufferAttribute(uvAttr, b);
          const uvC = new THREE.Vector2().fromBufferAttribute(uvAttr, c);
          uvDst.push(uvA.x, uvA.y, uvB.x, uvB.y, uvC.x, uvC.y);
        }
      };

      if (hit) {
        const bkt = bucket(matIndex);
        pushTri(bkt.positions, bkt.uvs);
        removedTris++;
      } else {
        pushTri(keepPositions, keepUVs);
      }
    }

    // Replace mesh geometry with kept triangles
    const keptGeom = new THREE.BufferGeometry();
    keptGeom.setAttribute("position", new THREE.Float32BufferAttribute(keepPositions, 3));
    if (keepUVs.length > 0)
      keptGeom.setAttribute("uv", new THREE.Float32BufferAttribute(keepUVs, 2));
    keptGeom.computeVertexNormals();
    keptGeom.computeBoundingBox();
    keptGeom.computeBoundingSphere();
    mesh.geometry.dispose();
    mesh.geometry = keptGeom;

    // Build removed section geometry
    const removedGeom = new THREE.BufferGeometry();
    const matIdxs = Array.from(removedByMat.keys()).sort((a, b) => a - b);
    const allPos = [];
    const allUv = [];
    let startVert = 0;
    for (const mIdx of matIdxs) {
      const { positions, uvs } = removedByMat.get(mIdx);
      const vCount = positions.length / 3;
      if (!vCount) continue;
      allPos.push(...positions);
      if (uvs.length === vCount * 2) allUv.push(...uvs);
      removedGeom.addGroup(startVert, vCount, mIdx);
      startVert += vCount;
    }
    removedGeom.setAttribute("position", new THREE.Float32BufferAttribute(allPos, 3));
    if (allUv.length > 0)
      removedGeom.setAttribute("uv", new THREE.Float32BufferAttribute(allUv, 2));
    removedGeom.computeVertexNormals();
    removedGeom.computeBoundingBox();
    removedGeom.computeBoundingSphere();

    const matForRemoved = Array.isArray(mesh.material)
      ? mesh.material
      : mesh.material || new THREE.MeshPhongMaterial({ color: 0xffffff });

    const removedMesh = new THREE.Mesh(removedGeom, matForRemoved);
    removedMesh.name = `removedSection_${mesh.name}_${Date.now()}`;
    removedMesh.position.copy(mesh.position);
    removedMesh.rotation.copy(mesh.rotation);
    removedMesh.scale.copy(mesh.scale);
    removedChunks.push(removedMesh);
  }

  if (removedChunks.length === 0) {
    console.warn("⚠ No triangles removed in any mesh.");
    return null;
  }

  const merged = mergeRemovedMeshes(removedChunks);
  console.log(`🪓 Created combined removed mesh from ${removedChunks.length} parts.`);
  return merged;
}

/**
 * Merge removed chunks into one mesh
 */
function mergeRemovedMeshes(meshes) {
  const mergedGeom = new THREE.BufferGeometry();
  const mats = [];
  let start = 0;
  const posAll = [];
  const uvAll = [];
  for (const m of meshes) {
    const g = m.geometry;
    const pos = g.attributes.position.array;
    posAll.push(...pos);
    if (g.attributes.uv) uvAll.push(...g.attributes.uv.array);
    for (const grp of g.groups)
      mergedGeom.addGroup(start + grp.start, grp.count, grp.materialIndex + mats.length);
    start += pos.length / 3;
    if (Array.isArray(m.material)) mats.push(...m.material);
    else mats.push(m.material);
  }
  mergedGeom.setAttribute("position", new THREE.Float32BufferAttribute(posAll, 3));
  if (uvAll.length > 0)
    mergedGeom.setAttribute("uv", new THREE.Float32BufferAttribute(uvAll, 2));
  mergedGeom.computeVertexNormals();
  mergedGeom.computeBoundingBox();
  mergedGeom.computeBoundingSphere();
  const mergedMesh = new THREE.Mesh(mergedGeom, mats);
  mergedMesh.name = `removedSection_combined_${Date.now()}`;
  return mergedMesh;
}
